import { useEffect } from "react"

import { Box, Button, CircularProgress, Typography } from "@mui/material"
import { useQueryStudentClassesById } from "api/reactQuery/queries/studentClasses"
import Dialog from "components/common/dialog/dialog"
import { useDialog } from "hooks/dialog"
import { useTranslation } from "react-i18next"
import { Navigate, Outlet, useLocation, useParams } from "react-router"
import { useNavigate } from "react-router-dom"

import { RoutePath } from "../Route.types"

const HelperProtectedRoute = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const params = useParams()
  const { t } = useTranslation()

  const {
    data: studentClassData,
    isSuccess,
    isError,
    error,
  } = useQueryStudentClassesById({
    id: Number(params.classId),
    options: {
      enabled: !!params.classId,
    },
  })

  const { isOpen, handleOpen } = useDialog()

  const handleGoBack = () => {
    if (location.key !== "default") {
      navigate(-1)
    } else {
      navigate(RoutePath.HOMEPAGE, { replace: true })
    }
  }

  const hasHelperAccess = () => {
    if (!studentClassData?.data) return false

    const classData = studentClassData.data

    if (location.pathname.includes("/banker")) {
      return classData.is_banker
    }
    if (location.pathname.includes("/storeClerk")) {
      return classData.is_store_clerk
    }
    if (location.pathname.includes("/hr-representative")) {
      return classData.is_hr_representative
    }

    return false
  }

  useEffect(() => {
    if (isSuccess && studentClassData?.data && !hasHelperAccess()) {
      handleOpen()
    }
  }, [isSuccess, studentClassData, location.pathname, handleOpen])

  if (isError) {
    const status = error?.response?.status

    if (status === 404) {
      return <Navigate to={RoutePath.PAGE404_NOT_FOUND} replace />
    }

    if (status === 401 || status === 403) {
      return <Navigate to={RoutePath.HOMEPAGE} replace />
    }

    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (!isSuccess) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
      >
        <CircularProgress />
      </Box>
    )
  }

  if (hasHelperAccess()) {
    return <Outlet />
  }

  return (
    <>
      <Box
        sx={{
          position: "fixed",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          zIndex: 1200,
        }}
      />

      <Dialog
        titleText={t("layout.noHelperAccess")}
        open={isOpen}
        onIconClose={false}
        disableEscapeKeyDown
        customActionButtonsSlot={null}
      >
        <Typography variant="subtitle1" mb={2}>
          {t("layout.youAreNotPermittedToAccessThisHelperRole")}
        </Typography>

        <Box gap="10px" display="flex" justifyContent="flex-end" mt={2}>
          <Button variant="outlined" onClick={handleGoBack}>
            {t("layout.goBack")}
          </Button>

          <Button
            variant="contained"
            onClick={() => navigate(RoutePath.HOMEPAGE, { replace: true })}
          >
            {t("layout.goToDashboard")}
          </Button>
        </Box>
      </Dialog>
    </>
  )
}

export default HelperProtectedRoute
